import { captureException } from "@sentry/nextjs"
import { unstable_rethrow } from "next/navigation"
import type {
  IBody,
  IJobCreate,
  ILbaItemLbaCompanyJson,
  ILbaItemLbaJobJson,
  ILbaItemPartnerJobJson,
  INewDelegations,
  INewSuperUser,
  IQuery,
  IRecruiterJson,
  IResponse,
  IRoutes,
} from "shared"
import { removeUndefinedFields } from "shared"
import type { ApplicationIntention } from "shared/constants/application"
import type { BusinessErrorCodes } from "shared/constants/error-codes"
import type { LBA_ITEM_TYPE } from "shared/constants/lbaitem"
import type { JOB_STATUS_ENGLISH } from "shared/models/job.model"
import type { ILbaCompanySearchField } from "shared/routes/update-lba-company.routes"
import type { Jsonify } from "type-fest"
import { ApiError, apiDelete, apiGet, apiPatch, apiPost, apiPut } from "./api.utils"

const errorHandler = (error: any): undefined => {
  if (error) {
    console.error("Erreur de l'API :", error)
  }
}

/**
 * Formulaire API
 */

export const getDelegationDetails = async (establishment_id: string, token: string) =>
  apiGet("/formulaire/delegation/:establishment_id", { params: { establishment_id }, headers: { authorization: `Bearer ${token}` } }).catch(errorHandler)
export const getFormulaire = async (establishment_id: string) => apiGet("/formulaire/:establishment_id", { params: { establishment_id } }).catch(errorHandler)
export const getEntrepriseManagedByCfa = async (cfaId: string, establishment_id: string) =>
  apiGet("/etablissement/cfa/:cfaId/entreprise/:establishment_id", { params: { establishment_id, cfaId } }).catch(errorHandler)
export const getFormulaireByToken = async (establishment_id: string, token: string) =>
  apiGet("/formulaire/:establishment_id/by-token", { params: { establishment_id }, headers: { authorization: `Bearer ${token}` } }).catch(errorHandler)

export const archiveFormulaire = async (establishment_id: string) => apiDelete("/formulaire/:establishment_id", { params: { establishment_id } }).catch(errorHandler)

/**
 * Offre API
 */
export const getOffre = async (jobId: string) => apiGet("/formulaire/offre/f/:jobId", { params: { jobId } })
export const createOffre = async (establishment_id: string, newOffre: IJobCreate) =>
  apiPost("/formulaire/:establishment_id/offre", { params: { establishment_id }, body: newOffre })
export const createOffreByToken = async (establishment_id: string, newOffre: IJobCreate, token: string) =>
  apiPost("/formulaire/:establishment_id/offre/by-token", { params: { establishment_id }, body: newOffre, headers: { authorization: `Bearer ${token}` } })
export const ameliorerTexteOffre = async (establishment_id: string, field: "job_description" | "job_employer_description", text: string) =>
  apiPost("/formulaire/:establishment_id/offre/ameliorer-texte", { params: { establishment_id }, body: { field, text } })
export const ameliorerTexteOffreByToken = async (establishment_id: string, field: "job_description" | "job_employer_description", text: string, token: string) =>
  apiPost("/formulaire/:establishment_id/offre/ameliorer-texte/by-token", { params: { establishment_id }, body: { field, text }, headers: { authorization: `Bearer ${token}` } })
export const viewOffreDelegation = async (jobId: string, siret: string, token: string) =>
  apiPatch(`/formulaire/offre/:jobId/delegation/view`, { params: { jobId }, querystring: { siret_formateur: siret }, headers: { authorization: `Bearer ${token}` } })
// once offres_emploi_lba are definitly stored in jobs partners, we can move this call to /jobs/:jobId/cancel
export const cancelOffre = async (jobId: string, token: string, data: IRoutes["put"]["/formulaire/offre/:jobId/cancel"]["body"]["_input"]) =>
  apiPut(`/formulaire/offre/:jobId/cancel`, { params: { jobId }, body: data, headers: { authorization: `Bearer ${token}` } })
export const cancelOffreFromAdmin = async (jobId: string, data: IRoutes["put"]["/formulaire/offre/f/:jobId/cancel"]["body"]["_input"]) =>
  apiPut("/formulaire/offre/f/:jobId/cancel", { params: { jobId }, body: data })
export const extendOffre = async (jobId: string, jobFields: Jsonify<IBody<IRoutes["put"]["/formulaire/offre/:jobId/extend"]>>) => {
  // @ts-expect-error
  const body: IBody<IRoutes["put"]["/formulaire/offre/:jobId/extend"]> = jobFields
  return apiPut(`/formulaire/offre/:jobId/extend`, { params: { jobId }, body })
}
export const notifyLbaJobDetailView = async (jobId: string) => await apiPost("/v1/jobs/matcha/:id/stats/view-details", { params: { id: jobId } })
export const notifyJobDetailViewV3 = async (job: ILbaItemLbaJobJson | ILbaItemLbaCompanyJson | ILbaItemPartnerJobJson) => {
  const jobId = getObjectId(job)
  if (!jobId) return
  try {
    return await apiPost("/v3/jobs/:id/stats/:eventType", { params: { id: jobId, eventType: "detail_view" } })
  } catch {
    // tracking non-critique, erreur ignorée intentionnellement
  }
}
export const notifyJobSearchViewV3 = async (ids: string[]) => {
  ids = ids.filter(lookLikeObjectId)
  if (!ids.length) return
  try {
    // keepalive : l'envoi déclenché au pagehide (cf. useSearchViewNotifier) doit survivre au déchargement
    return await apiPost("/v3/jobs/stats/search_view", { body: ids }, { keepalive: true })
  } catch {
    // tracking non-critique, erreur ignorée intentionnellement
  }
}
export const notifyJobPostulerV3 = async (job: ILbaItemLbaJobJson | ILbaItemLbaCompanyJson | ILbaItemPartnerJobJson) => {
  const jobId = getObjectId(job)
  if (!jobId) return
  try {
    return await apiPost("/v3/jobs/:id/stats/:eventType", { params: { id: jobId, eventType: "postuler_click" } })
  } catch {
    // tracking non-critique, erreur ignorée intentionnellement
  }
}
export const getRelatedEtablissementsFromRome = async ({ rome, latitude, longitude, limit }: { rome: string; latitude: number; longitude: number; limit: number }) =>
  apiGet(`/etablissement/cfas-proches`, { querystring: { rome, latitude, longitude, limit } })
export const createEtablissementDelegation = async ({ data, jobId }: { jobId: string; data: INewDelegations }) =>
  apiPost(`/formulaire/offre/:jobId/delegation`, { params: { jobId }, body: data })
export const createEtablissementDelegationByToken = async ({ data, jobId, token }: { jobId: string; data: INewDelegations; token: string }) =>
  apiPost(`/formulaire/offre/:jobId/delegation/by-token`, { params: { jobId }, body: data, headers: { authorization: `Bearer ${token}` } })

/**
 * User API
 */
export const getUser = async (userId: string, organizationId: string = "unused") => apiGet("/user/:userId/organization/:organizationId", { params: { userId, organizationId } })
export const getUserStatus = async (userId: string) => apiGet("/user/status/:userId", { params: { userId } })
export const getUserStatusByToken = async (userId: string, token: string) =>
  apiGet("/user/status/:userId/by-token", { params: { userId }, headers: { authorization: `Bearer ${token}` } })

export const activateUserRole = async (userId: string, organizationId: string) =>
  apiPost("/user/:userId/organization/:organizationId/activate", { params: { userId, organizationId } })

export const deactivateUserRole = async (userId: string, organizationId: string, reason: string) =>
  apiPost("/user/:userId/organization/:organizationId/deactivate", { params: { userId, organizationId }, body: { reason } })

export const notifyNotMyOpcoUserRole = async (userId: string, organizationId: string, reason: string) =>
  apiPost("/user/:userId/organization/:organizationId/not-my-opco", { params: { userId, organizationId }, body: { reason } })

export const createSuperUser = async (user: INewSuperUser) => apiPost("/admin/users", { body: user })

/**
 * Admin - jobs_partners
 */
export const getJobsPartnersForAdmin = async (params: { partner_label?: string[]; offer_status?: JOB_STATUS_ENGLISH[]; id?: string; limit?: number; offset?: number }) =>
  apiGet("/admin/jobs-partners", { querystring: removeUndefinedFields(params) })

export const activateJobPartner = async (id: string) => apiPost("/admin/jobs-partners/:id/activate", { params: { id } })

export const deactivateJobPartner = async (id: string, reason: string) => apiPost("/admin/jobs-partners/:id/deactivate", { params: { id }, body: { reason } })

export const setJobPartnerClassification = async (id: string, classification: "publish" | "unpublish") =>
  apiPost("/admin/jobs-partners/:id/classification", { params: { id }, body: { classification } })

/**
 * Modération des offres libres (#5499)
 */
export const getModerationQueue = async (querystring: IQuery<IRoutes["get"]["/moderation/jobs"]>) => apiGet("/moderation/jobs", { querystring: removeUndefinedFields(querystring) })

export const getModerationSummaries = async (ids: string[]) => apiGet("/moderation/jobs/summary", { querystring: { ids } })

export const getModerationJob = async (id: string) => apiGet("/moderation/jobs/:id", { params: { id } })

export const validateModerationJob = async (id: string, moderationId: string) => apiPost("/moderation/jobs/:id/validate", { params: { id }, body: { moderation_id: moderationId } })

export const refuseModerationJob = async (id: string, body: IBody<IRoutes["post"]["/moderation/jobs/:id/refuse"]>) =>
  apiPost("/moderation/jobs/:id/refuse", { params: { id }, body })

export const reclassifyModerationJob = async (id: string) => apiPost("/moderation/jobs/:id/reclassify", { params: { id } })

export const reclassifyFailedModerationJobs = async () => apiPost("/moderation/jobs/reclassify", {})

// Temporaire, en attendant d'ajuster le modèle pour n'avoir qu'une seul source de données pour les entreprises
/**
 * KBA 20230511 : (migration db) : casting des valueurs coté collection recruiter, car les champs ne sont plus identiques avec la collection userRecruteur.
 */
export const updateUserWithAccountFields = async (userId: string, user: IBody<IRoutes["put"]["/user/:userId"]>) => {
  await apiPut("/user/:userId", { params: { userId }, body: user })
}

export const updateEntrepriseAdmin = async (userId: string, user: IBody<IRoutes["put"]["/admin/users/:userId/organization/:siret"]>, siret = "unused") => {
  await apiPut("/admin/users/:userId/organization/:siret", { params: { userId, siret }, body: user })
}

// modification des informations de contact d'un recruiter par le CFA gestionnaire
export const updateEntrepriseCFA = async (establishmentId: string, values: IBody<IRoutes["post"]["/formulaire/:establishment_id/informations"]>) => {
  await apiPost("/formulaire/:establishment_id/informations", { params: { establishment_id: establishmentId }, body: values })
}

export const updateUser = async (userId: string, user: any) => {
  await apiPut("/admin/users/:userId", { params: { userId }, body: user })
}

/**
 * Auth API
 */
export const sendValidationLink = async (userId: string, token: string) =>
  await apiPost("/login/:userId/resend-confirmation-email", { params: { userId }, headers: { authorization: `Bearer ${token}` } })

/**
 * Etablissement API
 */
export const getEntreprisesManagedByCfa = async (cfaId: string): Promise<IRecruiterJson[]> => apiGet("/etablissement/cfa/:cfaId/entreprises", { params: { cfaId } })
export const getCfaInformation = async (siret: string) => {
  try {
    const data = await apiGet("/etablissement/cfa/:siret", { params: { siret } })
    return { statusCode: 200, data, error: false }
  } catch (error) {
    unstable_rethrow(error)
    if (error instanceof ApiError && error.context?.statusCode >= 400) {
      const { errorData, statusCode, message } = error.context
      if (error.context.statusCode >= 500) {
        captureException(error)
      }
      return { statusCode, message, data: errorData, error: true }
    } else {
      captureException(error)
      return { statusCode: 500, message: "unkown error", error: true }
    }
  }
}
export const validateCfaCreation = async (siret: string) => apiGet("/etablissement/cfa/:siret/validate-creation", { params: { siret } })

type EntrepriseInfos = IResponse<IRoutes["get"]["/etablissement/entreprise/:siret"]>
export const getEntrepriseInformation = async (
  siret: string,
  { cfa_delegated_siret, skipUpdate }: { cfa_delegated_siret?: string; skipUpdate?: boolean } = {}
): Promise<{ statusCode: 200; data: EntrepriseInfos; error: false } | { statusCode: number; message: string; data?: { errorCode?: BusinessErrorCodes }; error: true }> => {
  try {
    const data = await apiGet(
      "/etablissement/entreprise/:siret",
      { params: { siret }, querystring: removeUndefinedFields({ cfa_delegated_siret, skipUpdate: skipUpdate ? "true" : "false" }) },
      { timeout: 7000 }
    )
    return { statusCode: 200, data, error: false }
  } catch (error: unknown) {
    unstable_rethrow(error)
    if (error instanceof ApiError && error.context?.statusCode >= 400) {
      const { errorData, statusCode, message } = error.context
      if (error.context.statusCode >= 500) {
        captureException(error)
      }
      return { statusCode, message, data: errorData, error: true }
    } else {
      captureException(error)
      return { statusCode: 500, message: "unkown error", error: true }
    }
  }
}
export const getEntrepriseOpco = async (siret: string) => {
  try {
    const data = await apiGet("/etablissement/entreprise/:siret/opco", { params: { siret } }, { timeout: 7000 })
    return data
  } catch (error) {
    unstable_rethrow(error)
    captureException(error)
    return null
  }
}

export const getPrdvContext = async (cleMinistereEducatif: string, referrer: string | null = "lba") => {
  try {
    const data = await apiGet("/_private/appointment", { querystring: { cleMinistereEducatif, referrer: referrer || "lba" } }, { timeout: 7000 })
    return data
  } catch (error) {
    // Rend la main à Next avant tout traitement applicatif. Avec `cacheComponents`, un fetch non
    // mis en cache pendant le prerender renvoie une promesse suspendue qui rejette quand le
    // prerender s'interrompt (digest HANGING_PROMISE_REJECTION) : capturée ici, elle produirait un
    // event Sentry par requête sur /rdva (mesuré : 20 467 sur 5 jours, 80 % du volume d'erreurs de
    // l'UI, LBA-UI-5CVZZZZZZG501). unstable_rethrow relaie aussi notFound() et redirect().
    unstable_rethrow(error)
    const isExpectedError = error instanceof ApiError && error.context.statusCode >= 400 && error.context.statusCode < 500
    if (isExpectedError) {
      return null
    }
    captureException(error)
    throw error
  }
}

export const getCompanyContactInfo = async (siret: string) => {
  const data = await apiGet("/lbacompany/:siret/contactInfo", { params: { siret } })
  return data
}

export const searchLbaCompanies = async (search: string, field: ILbaCompanySearchField) => {
  const data = await apiGet("/admin/lba-companies", { querystring: { search, field } })
  return data
}

export const putCompanyContactInfo = async ({ siret, phone, email }: { siret: string; phone: string; email: string }) => {
  const data = await apiPut("/lbacompany/:siret/contactInfo", { params: { siret }, body: { phone, email } })
  return data
}

export const getApplicationCompanyEmailAddress = async (token: string) => {
  const data = await apiGet("/application/company/email", { querystring: { token } })
  return data
}

export const getApplicationDataForIntention = async (applicationId: string, intention: ApplicationIntention, token: string) => {
  const data = await apiGet("/application/intention/schedule/:id", {
    params: { id: applicationId },
    querystring: { intention },
    headers: {
      authorization: `Bearer ${token}`,
    },
  })
  return data
}

export const getRomeDetail = async (rome: string) => apiGet("/rome/detail/:rome", { params: { rome } })

/**
 * Administration OPCO
 */

export const getOpcoUsers = async () => apiGet("/user/opco", {})

export const reportLbaItem = async (itemId: string, type: LBA_ITEM_TYPE, reason: string, reasonDetails: string | undefined) =>
  apiPost("/report-company", { querystring: { type, itemId }, body: { reason, reasonDetails } })

export const getEligibleTrainingsForAppointments = async (siret: string) =>
  apiGet("/admin/eligible-trainings-for-appointment/etablissement-formateur-siret/:siret", { params: { siret: siret } })

export const getEtablissement = async (siret: string) => apiGet("/admin/etablissements/siret-formateur/:siret", { params: { siret: siret } })

export async function unsubscribeCompany({ email, reason }: { email: string; reason: string }) {
  return apiPost("/unsubscribe", { body: { email, reason } })
}

export async function unsubscribeCompanySirets({ email, reason, sirets }: { email: string; reason: string; sirets: string[] }) {
  return apiPost("/unsubscribe/sirets", { body: { email, reason, sirets } })
}

export async function sendIntentionComment(id: string, token: string, body: IBody<IRoutes["post"]["/application/intentionComment/:id"]>) {
  return apiPost("/application/intentionComment/:id", {
    params: { id },
    body,
    headers: {
      authorization: `Bearer ${token}`,
    },
  })
}

export async function cancelIntentionComment(id: string, token: string) {
  return apiPost("/application/intention/cancel/:id", {
    params: { id },
    headers: {
      authorization: `Bearer ${token}`,
    },
  })
}

export function lookLikeObjectId(id: string): boolean {
  return /^[0-9a-f]{24}$/gi.test(id)
}

export function getObjectId(job: ILbaItemLbaJobJson | ILbaItemLbaCompanyJson | ILbaItemPartnerJobJson): string | null {
  const { id, recipient_id } = job
  if (lookLikeObjectId(id)) return id
  const [, part] = recipient_id.split("_")
  if (lookLikeObjectId(part)) return part
  return null
}
